// Official-ghost selection (design §2.6 / §3.2, TECH_SPEC §4bis.13) — the
// SINGLE source for the bootstrap mascot's difficulty. The server's cold-start
// ghost and the playtest harness's beatability metric both use this; changing
// the ladder or band in one place changes both.
//
// Calibration history: the design wants 40–50% of first-timers to beat the
// ghost (§2.6). v1 band [300, 650] produced 8–20% beatability; v2/v3
// recalibrated to [200, 300] against the average-tier p40 of ≈230–255 m.
// v4's easier opening (§2.6 dual-path) lifts average-tier scores — 62–68% of
// avg-tier bots exceed 300 m — so the v4 band moves up (candidate [300, 420],
// calibrated to [300, 430]), targeting mid-band beatability ≈45–50% on the
// W36–W40 seeds (calibration loops logged in rnd/iterations/003/outcome.md).
//
// Bands are per GenVersion and the old version's band is FROZEN: an official
// ghost regenerated for a v3 track must land where v3 ghosts always landed —
// band drift on old tracks would corrupt racing silently (§4bis.24; pinned by
// test/fixtures/v3-tracks.json).

import { SIM_VERSION } from './constants';
import { runBot, type BotConfig } from './bot';
import type { GenVersion, InputEvent } from './types';

export const GHOST_BANDS: Record<GenVersion, readonly [number, number]> = {
  '3': [200, 300], // FROZEN
  // Calibrated 2026-08-30 (loop 2 of 3): [300,420] left seed 1937755196 with an
  // out-of-band 426 m pick; widening to 430 puts all five W36–W40 picks in band
  // and lands beatability at 51.7/40/53.3/53.3/41.7% (mean 48.0, the pinned
  // mid-band 45–50% target). [320,420] (loop 3) changed nothing vs loop 1.
  '4': [300, 430],
};
for (const band of Object.values(GHOST_BANDS)) Object.freeze(band);
Object.freeze(GHOST_BANDS);

/** Band for a persisted sim_version string; unknown versions use the current band. */
export function ghostBand(gen: string = SIM_VERSION): readonly [number, number] {
  return GHOST_BANDS[gen as GenVersion] ?? GHOST_BANDS[SIM_VERSION as GenVersion]!;
}

/** The CURRENT version's band (what the playtest harness scores against). */
export const GHOST_BAND_MIN_M = ghostBand()[0];
export const GHOST_BAND_MAX_M = ghostBand()[1];
export const GHOST_BOT_SEEDS = 12;

export const GHOST_LADDER: readonly BotConfig[] = [
  { reaction: 4, errorPermille: 260 },
  { reaction: 3, errorPermille: 300 },
  { reaction: 4, errorPermille: 200 },
  { reaction: 5, errorPermille: 240 },
];

export interface GhostPick {
  scoreM: number;
  inBand: boolean;
  reaction: number;
  errorPermille: number;
  botSeed: number;
  inputs: InputEvent[];
}

/**
 * First ladder run scoring inside the band wins; otherwise the candidate
 * closest to the band's midpoint. Deterministic per track seed.
 */
export function pickOfficialGhostRun(trackSeed: number, gen: string = SIM_VERSION): GhostPick | null {
  const [bandMin, bandMax] = ghostBand(gen);
  const mid = (bandMin + bandMax) / 2;
  let best: GhostPick | null = null;
  for (const cfg of GHOST_LADDER) {
    for (let botSeed = 1; botSeed <= GHOST_BOT_SEEDS; botSeed++) {
      const { inputs, result } = runBot(trackSeed, { ...cfg, seed: botSeed }, gen);
      if (!result.ok) continue;
      const cand: GhostPick = {
        scoreM: result.distanceM,
        inBand: result.distanceM >= bandMin && result.distanceM <= bandMax,
        reaction: cfg.reaction ?? 0,
        errorPermille: cfg.errorPermille ?? 0,
        botSeed,
        inputs,
      };
      if (cand.inBand) return cand;
      if (best === null || Math.abs(cand.scoreM - mid) < Math.abs(best.scoreM - mid)) best = cand;
    }
  }
  return best;
}
